/**
 * Hook personnalisé pour gérer les actions sur une demande d'inscription
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { 
  useUpdateReponseDemandeInscriptionMutation, 
  usePriseEnChargeDemandeInscriptionMutation,
  useUpdateDemandeInscriptionMutation 
} from "../../../../redux/slice/api/BO/demandeInscriptionApi";
import { useLazyFetchBOSocieteAllMarquesQuery } from "../../../../redux/slice/api/BO/societeApi";
import { IInscription } from "../../../../models/interfaces/IInscription";
import { ITypeRefusDemandeInscription } from "../../../../models/interfaces/ITypeRefusDemandeInscriptionList";
import { Type_Return_Message } from "../../../../models/interfaces/IReturn_Parent";
import { ETATS_DEMANDE } from "../../../../config/helpers";

export const useInscriptionActions = (inscriptionId: number, refetchInscription?: () => void | Promise<void>) => {
  const navigate = useNavigate();
  const [priseEnCharge, setPriseEnCharge] = useState(false);
  const [showRefuseModal, setShowRefuseModal] = useState(false);
  const [explicationRefus, setExplicationRefus] = useState("");
  const [selectedTypeRefus, setSelectedTypeRefus] = useState<ITypeRefusDemandeInscription | null>(null);
  
  const [updateReponse, { isLoading: isUpdating }] = useUpdateReponseDemandeInscriptionMutation();
  const [priseEnChargeApi, { isLoading: isPriseEnChargeUpdating }] = usePriseEnChargeDemandeInscriptionMutation();
  const [updateDemande, { isLoading: isSaving }] = useUpdateDemandeInscriptionMutation();
  const [fetchMarques] = useLazyFetchBOSocieteAllMarquesQuery();

  const handleSaveAndContinue = async (formData: IInscription, typeCompte?: number, shouldValidate: boolean = false) => {
    try {
      // Si on doit valider (activer le compte), vérifier d'abord qu'il y a au moins une marque
      if (shouldValidate && formData.IdSociete) {
        try {
          const marquesResult = await fetchMarques(formData.IdSociete).unwrap();
          if (marquesResult.Marques.length === 0) {
            toast.error("Il faut renseigner au moins une marque avant d'activer le compte", {
              style: {
                backgroundColor: '#dc3545',
                color: 'white'
              }
            });
            return false;
          }
        } catch (error) {
          toast.error("Erreur lors de la vérification des marques");
          console.error("Erreur:", error);
          return false;
        }
      }
      
      const result: any = await updateReponse({
        Id: inscriptionId,
        IdPays: formData.IdPays,
        Siren: formData.Siren,
        RaisonSociale: formData.RaisonSociale,
        RaisonSocialeSiren: formData.RaisonSocialeSiren,
        SiteInternet: formData.SiteInternet,
        Adresse: formData.Adresse,
        CodePostal: formData.CodePostal,
        Ville: formData.Ville,
        IdCivilite: formData.IdCivilite,
        Nom: formData.Nom,
        Prenom: formData.Prenom,
        Email: formData.Email,
        Telephone: formData.Telephone,
        IdFonction: formData.IdFonction,
        GLNEditeur: formData.GLNEditeur,
        Marque: formData.Marque,
        IdSecteurActivite: formData.IdSecteurActivite,
        ISBN: formData.ISBN,
        ISBN13EANComplet: formData.ISBN13EANComplet,
        GLNDiffuseur: formData.GLNDiffuseur,
        Commentaires: formData.Commentaires,
        Fichiers: formData.Fichiers,
        Idetatdemandeinscription: shouldValidate ? ETATS_DEMANDE.VALIDE : ETATS_DEMANDE.AFFECTATION_MARQUES,
        Explicationrefus: "",
        IdTypeDeCompteSociete: typeCompte,
        ActiverCompte: null
      }).unwrap();
      
      // Vérifier si l'API retourne une erreur dans la réponse
      if (result?.Return_IdType_Message === Type_Return_Message.Erreur) {
        toast.error(result.Return_Message || "Erreur lors de la création de la société", {
          style: {
            backgroundColor: '#dc3545',
            color: 'white'
          }
        });
        return false;
      }
      
      if (shouldValidate) {
        toast.success("La demande d'inscription a été validée avec succès");
        navigate("/admin/inscriptions");
      } else {
        toast.success("Le compte société et compte admin créés avec succès. Maintenant il faut affecter les marques");
        
        // Recharger les données pour mettre à jour IdSociete et autres champs
        if (refetchInscription) {
          await refetchInscription();
        }
      }
      
      return true;
    } catch (error) {
      toast.error(shouldValidate ? "Erreur lors de la validation de la demande" : "Erreur lors de la sauvegarde des données");
      console.error("Erreur:", error);
      return false;
    }
  };

  const handleSave = async (formData: IInscription) => {
    try {
      await updateDemande({
        Id: inscriptionId,
        IdPays: formData.IdPays,
        Siren: formData.Siren,
        RaisonSociale: formData.RaisonSociale,
        RaisonSocialeSiren: formData.RaisonSocialeSiren,
        SiteInternet: formData.SiteInternet,
        Adresse: formData.Adresse,
        CodePostal: formData.CodePostal,
        Ville: formData.Ville,
        IdCivilite: formData.IdCivilite,
        Nom: formData.Nom,
        Prenom: formData.Prenom,
        Email: formData.Email,
        Telephone: formData.Telephone,
        IdFonction: formData.IdFonction,
        GLNEditeur: formData.GLNEditeur,
        Marque: formData.Marque,
        IdSecteurActivite: formData.IdSecteurActivite,
        ISBN: formData.ISBN,
        ISBN13EANComplet: formData.ISBN13EANComplet,
        GLNDiffuseur: formData.GLNDiffuseur,
        Commentaires: formData.Commentaires,
        IdTypeDeCompteSociete: formData.IdTypeDeCompteSociete,
        AcceptationCGU: formData.AcceptationCGU
      }).unwrap();
      
      toast.success("Les modifications ont été enregistrées avec succès");
      
      // Recharger les données
      if (refetchInscription) {
        await refetchInscription();
      }
      
      return true;
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement des modifications");
      console.error("Erreur:", error);
      return false;
    }
  };

  const handlePriseEnCharge = async (checked: boolean) => {
    try {
      await priseEnChargeApi({
        Id: inscriptionId,
        IdEtatDemandeInscription:ETATS_DEMANDE.EN_COURS
      }).unwrap();
      
      setPriseEnCharge(checked);
      toast.success(checked ? "La demande vous est attribuée" : "La demande ne vous est plus attribuée");
      
      // Recharger les données après prise en charge
      if (refetchInscription) {
        refetchInscription();
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
      console.error("Erreur:", error);
      setPriseEnCharge(!checked); // Revert on error
    }
  };

  const handleRefuser = () => {
    setShowRefuseModal(true);
  };

  const confirmRefuser = async () => {
    if (!explicationRefus.trim()) {
      toast.warning("Veuillez saisir une explication pour le refus");
      return;
    }

    if (!selectedTypeRefus) {
      toast.warning("Veuillez sélectionner un type de refus");
      return;
    }

    try {
      await updateReponse({
        Id: inscriptionId,
        Idetatdemandeinscription: ETATS_DEMANDE.REFUSE,
        Explicationrefus: explicationRefus,
        IdTypeRefusDemandeInscription: selectedTypeRefus.Id
      }).unwrap();
      
      toast.success("La demande d'inscription a été refusée");
      navigate("/admin/inscriptions");
    } catch (error) {
      toast.error("Erreur lors du refus de la demande");
      console.error("Erreur:", error);
    }
  };

  const handleActiverCompte = async (typeCompte?: number) => {
    try {
      await updateReponse({
        Id: inscriptionId,
        Idetatdemandeinscription: ETATS_DEMANDE.VALIDE,
        ActiverCompte: true,
        IdTypeDeCompteSociete: typeCompte
      }).unwrap();
      
      toast.success("Le compte a été activé avec succès");
      navigate("/admin/inscriptions");
    } catch (error) {
      toast.error("Erreur lors de l'activation du compte");
      console.error("Erreur:", error);
    }
  };

  const handleDesactiverCompte = async () => {
    try {
      await updateReponse({
        Id: inscriptionId,
        ActiverCompte: false
      }).unwrap();
      
      toast.success("Le compte a été désactivé avec succès");
      navigate("/admin/inscriptions");
    } catch (error) {
      toast.error("Erreur lors de la désactivation du compte");
      console.error("Erreur:", error);
    }
  };

  return {
    priseEnCharge,
    setPriseEnCharge,
    showRefuseModal,
    setShowRefuseModal,
    explicationRefus,
    setExplicationRefus,
    selectedTypeRefus,
    setSelectedTypeRefus,
    isUpdating: isUpdating || isPriseEnChargeUpdating,
    isSaving,
    handleSave,
    handleSaveAndContinue,
    handlePriseEnCharge,
    handleRefuser,
    confirmRefuser,
    handleActiverCompte,
    handleDesactiverCompte
  };
};
